import { Component, OnInit, OnDestroy, input, output, signal, effect } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { GridColumnData } from 'src/app/features/grid/classes/grid-column-data.class';
import { GridSettings } from 'src/app/features/grid/classes/grid-settings.model';
import { IdentifierService } from '../../services/identifier.service';
import { ClientIntegrationService } from '../../services/client-integration.service';
import { DestinationEndpoint } from '../../models/destination-endpoint';
import { IntegrationIdentifier } from '../../models/integration-identifier';
import { ClientInformation } from '../../models/client-information';
import { Integration } from '../../models/integration';
import { TypeConstantService } from '../../services/type-constant.service';
import { ToastService } from '../../services/toast.service';
import { environment } from '../../../environments/environment';
import { User } from 'src/app/models/user';
import { ClientContract } from 'src/app/models/client-contract';
import { createEditButton, createCustomButton, createDeleteButton } from 'src/app/features/grid/classes/grid-config.types';
@Component({
  selector: 'app-identifier-view',
  templateUrl: './identifier-view.component.html',
  styleUrls: ['./identifier-view.component.scss'],
  providers: [ClientIntegrationService, IdentifierService, TypeConstantService],
  standalone: false,
})
export class IdentifierViewComponent implements OnInit, OnDestroy {

  componentDestroyed$ = new Subject<boolean>();
  client = input<ClientInformation>();
  integration = input<Integration>();
  baseData = input<IntegrationIdentifier[]>([]);
  clientContracts = input<ClientContract[]>([]);
  identifier = output<IntegrationIdentifier>();
  deleteIdentifier = output<IntegrationIdentifier>();

  inAddMode = false;
  showAddUpdateForm = false;
  generatedNewIdentifier = false;
  loadingWorkbook = false;
  runningGeneratingIdentifiers = false;
  activeUser: User;
  allDestinations: DestinationEndpoint[];
  selectedIdentifier: IntegrationIdentifier;
  localBaseData: IntegrationIdentifier[] = [];

  public gridSettings = signal<GridSettings>(null);
  public gridLoading = signal<boolean>(true);
  settingsReady = false;
  
  csDataSwapRoles: string[] = [
    'LOSTalker.Integration.CompenSafe.DataSwap',
    'LOSTalker.Integration.CompenSafe.FastFetch',
    'LOSTalker.Integration.CompenSafe',
  ];

  lgDataSwapRoles: string[] = [
    'LOSTalker.Integration.LimeGear.DataSwap',
    'LOSTalker.Integration.LimeGear.FastFetch',
    'LOSTalker.Integration.LimeGear',
  ];

  constructor(
    private identifierService: IdentifierService,
    private typeService: TypeConstantService,
    protected toastService: ToastService,
    private user: User,
  ) {
    this.allDestinations = [];
    this.activeUser = user;

    effect(() => {
      const currentIntegration = this.integration();
      if (currentIntegration != null) {
        this.changeIntegration(currentIntegration);
      }
    });
  }

  ngOnInit(): void {
    this.setupGrid();
    this.getDestinations();
  }

  setupGrid(): void {
    this.gridSettings.set(new GridSettings({
      data: [],
      loading: this.gridLoading,
      columns: [
        new GridColumnData({ field: 'destinationName', header: 'Destination', width: '130px', minWidth: '50px'}),
        new GridColumnData({ field: 'identifierIdValue', header: 'Id Value', width: '50px', minWidth: '50px'}),
        new GridColumnData({ field: 'identifierNameValue', header: 'Name Value', width: '150px', minWidth: '50px'}),
        new GridColumnData({ field: 'createdDate', header: 'Created', type:'date', format: 'yyyy-MM-dd hh:mm:ss a', width: '150px', minWidth: '50px'}),
        new GridColumnData({ field: 'updatedDate', header: 'Updated', type:'date', format: 'yyyy-MM-dd hh:mm:ss a', width: '150px', minWidth: '50px'}),
        new GridColumnData({ field: 'canFastFetch', header: 'Can FastFetch', width: '130px', minWidth: '50px'}),
        new GridColumnData({ field: 'canDataSwap', header: 'Can DataSwap', width: '130px', minWidth: '50px'}),
        new GridColumnData({ field: 'comments', header: 'Comments', width: '200px', minWidth: '50px'}),
      ],
      sortMode: 'single',
      dataKey: 'identifierIdValue',
      export: {
        enabled: true,
        formats: ['csv'],
        fileName: 'Clients',
      },
      selection: {
        enabled: true,
        mode: 'single',
        selectionType: 'row',
      },
      commandsColumn: {
        enabled: true,
        headerText: 'Actions',
        layout: 'horizontal',
        buttonSpacing: '0.25rem',
        buttons: [
          createEditButton(),
          createCustomButton('workbook', 'Get Workbook', {onClick: (rowData, _commandType) => { this.generateWorkbook(rowData); }}),
          createDeleteButton(),
        ],
      },
    }));
    this.settingsReady = true;
  }

  updateGrid(): void {
    this.gridSettings.set(new GridSettings({
      ...this.gridSettings(),
      data: this.localBaseData,
      loading: this.gridLoading,
    }));
  }

  public changeIntegration(integration: Integration) {
    this.gridLoading.set(true);
    this.identifierService.getIntegrationIdentifiers(integration.integrationId)
    .pipe(takeUntil(this.componentDestroyed$))
    .subscribe(data => {
      this.localBaseData = data;
      this.gridLoading.set(false);
      this.updateGrid();
    });
  }

  ngOnDestroy(): void {
    this.componentDestroyed$.next(true);
    this.componentDestroyed$.complete();
  }

  private reloadData(flag = false): void {
    this.gridLoading.set(true);
    this.identifierService.getIntegrationIdentifiers(this.integration().integrationId)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe(data => {
        this.localBaseData = data;
        this.gridLoading.set(false);
        this.updateGrid();
      });
  }

  private outputIdentifier(event: any): void {
    this.identifier.emit(event);
  }
  
  editIdentifier(event: IntegrationIdentifier) {
    this.selectedIdentifier = event;
    this.inAddMode = false;
    this.showAddUpdateForm = true;
  }

  public addIdentifier() {
    this.selectedIdentifier = null;
    this.inAddMode = true;
    this.showAddUpdateForm = true;
  }

  public deleteIntegrationIdentifier(event: IntegrationIdentifier) {
    this.inAddMode = false;
    this.selectedIdentifier = event;

    var blockingContracts = this.clientContracts().filter(contract => (!contract.disabled
          && this.selectedIdentifier.identifierIdValue == contract.identifierIdValue
          && this.selectedIdentifier.destinationId == contract.destinationEndpointId));
    if (blockingContracts.length > 0) {
      this.toastService.toastCreate('Active Client Contract found matching identifier ' + this.selectedIdentifier.identifierIdValue + '. Disable matching contract and refresh page.', 'Warning', {
        autoClose: false,
      });
      return;
    }

    const confirmMsg =
      'Are you sure you want to delete the selected identifier?';
    if (window.confirm(confirmMsg)) {
      this.localBaseData = this.localBaseData.filter(identifier => identifier != this.selectedIdentifier)
      this.updateGrid();
      this.deleteIdentifier.emit(this.selectedIdentifier);
    }
  }

  public addUpdateFormFinished(event: any) {
    this.showAddUpdateForm = false;

    if (this.runningGeneratingIdentifiers && event == 'generated') {
      this.reloadData(true);
    }
    else {
      this.reloadData();
    }
  }

  checkAccess(role: string[]): boolean {
    return this.user.checkAccess(role);
  }

  generateWorkbook(event: IntegrationIdentifier) {
    const identifier = event;
    this.loadingWorkbook = true;
    const csDestination = this.allDestinations.find(d => d.endpointName == 'CSDataSwap');
    const maDestination = this.allDestinations.find(d => d.endpointName == 'LGDataSwap');

    if((!this.activeUser.checkAccess(this.csDataSwapRoles) && identifier.destinationId == csDestination.destinationEndpointId)) {
      this.toastService.toastCreate('You do not have access to the CSDataSwap destination. Please contact an administrator to generate the workbook.', 'Warning');
      this.loadingWorkbook = false;
      return;
    }
    else if(!this.activeUser.checkAccess(this.lgDataSwapRoles) && identifier.destinationId == maDestination.destinationEndpointId) {
      this.toastService.toastCreate('You do not have access to the LGDataSwap destination. Please contact an administrator to generate the workbook.', 'Warning');
      this.loadingWorkbook = false;
      return;
    }
    else if(this.activeUser.checkAccess(['LOSTalker.Integration'])){
      this.identifierService.generateIntegrationWorkbook(identifier)
      .subscribe(blob => {
        this.identifierService.downloadExcelResult(blob, identifier);
        this.loadingWorkbook = false;
      });
    }
    else {
      const selectedDest = this.allDestinations.find(d => d.destinationEndpointId == identifier.destinationId);
      this.toastService.toastCreate('You do not have access to the' + selectedDest.endpointName + 'destination. Please contact an administrator to generate the workbook.', 'Warning');
      this.loadingWorkbook = false;
      return;
    }
  }

  idDestinationsChecked = 0;
  generateIdentifier(): void {
    this.runningGeneratingIdentifiers = true;

    this.generatedNewIdentifier = true;
    const date: Date = new Date();
    let continueTesting = true;
    this.idDestinationsChecked = 0;

    const domParser = new DOMParser();
    const base = domParser.parseFromString(this.integration().xmlConfig, 'text/xml');

    const tmpIdentifier: IntegrationIdentifier = new IntegrationIdentifier();
    tmpIdentifier.integrationId = this.integration().integrationId;
    tmpIdentifier.createdDate = date;
    tmpIdentifier.updatedDate = date;
    tmpIdentifier.canDataSwap = false;
    tmpIdentifier.canFastFetch = false;
    console.log(this.localBaseData);
    if (this.localBaseData.filter(i => i.destinationName == 'LGDataSwap').length > 0) {
      if (environment.enableDebuggingTools) {
        console.log('LGDataSwap identifier already exists. Use edit to update.');
      }
    }
    else if (base.getElementsByTagName('LimeGear').length > 1) {
      this.toastService.toastCreate('Too many LimeGear elements exist to generate identifier. Update and try again.', 'Warning');
    }
    else if (base.getElementsByTagName('LimeGear').length == 1) {
      continueTesting = false;

      tmpIdentifier.identifierIdValue = base.getElementsByTagName('LimeGear')[0].getAttribute('CompanyId');
      tmpIdentifier.identifierNameValue = base.getElementsByTagName('LimeGear')[0].getAttribute('LimeGearCompany');
      tmpIdentifier.identifierKeyValue = base.getElementsByTagName('LimeGear')[0].getAttribute('LimeGearKey');

      if (this.allDestinations != null && this.allDestinations.length > 0) {
        const dest: DestinationEndpoint = this.allDestinations.filter(d => d.endpointName == 'LGDataSwap')[0];
        tmpIdentifier.destinationId = dest.destinationEndpointId;
        tmpIdentifier.destinationName = dest.endpointName;
      }
    }

    if (continueTesting) {
      if (this.localBaseData.filter(i => i.destinationName == 'CSDataSwap').length > 0) {
        if (environment.enableDebuggingTools) {
          console.log('CSDataSwap identifier already exists. Use edit to update.');
        }
      }
      else if (base.getElementsByTagName('CSDataSwap').length > 1) {
        this.toastService.toastCreate('Too many CSDataSwap elements exist to generate identifier. Update and try again.', 'Warning');
      }
      else if (base.getElementsByTagName('CSDataSwap').length == 1) {
        continueTesting = false;

        tmpIdentifier.identifierIdValue = base.getElementsByTagName('CSDataSwap')[0].getAttribute('CompanyId');
        tmpIdentifier.identifierNameValue = base.getElementsByTagName('CSDataSwap')[0].getAttribute('CSDataSwapCompany');
        tmpIdentifier.identifierKeyValue = base.getElementsByTagName('CSDataSwap')[0].getAttribute('CSDataSwapKey');

        if (this.allDestinations != null && this.allDestinations.length > 0) {
          const dest: DestinationEndpoint = this.allDestinations.filter(d => d.endpointName == 'CSDataSwap')[0];
          tmpIdentifier.destinationId = dest.destinationEndpointId;
          tmpIdentifier.destinationName = dest.endpointName;
        }
      }
    }

    if (continueTesting) {
      if (this.localBaseData.filter(i => i.destinationName == 'Surefire3').length > 0) {
        if (environment.enableDebuggingTools) {
          console.log('Surefire3 identifier already exists. Use edit to update.');
        }
      }
      else if (base.getElementsByTagName('Surefire3').length > 1) {
        this.toastService.toastCreate('Too many Surefire3 elements exist to generate identifier. Update and try again.', 'Warning');
      }
      else if (base.getElementsByTagName('Surefire3').length == 1) {
        continueTesting = false;
        tmpIdentifier.canDataSwap = false;

        tmpIdentifier.identifierIdValue = '';
        tmpIdentifier.identifierNameValue = base.getElementsByTagName('Surefire3')[0].getAttribute('MappingTag');
        tmpIdentifier.identifierKeyValue = base.getElementsByTagName('Surefire3')[0].getAttribute('ApiKey');

        if (this.allDestinations != null && this.allDestinations.length > 0) {
          const dest: DestinationEndpoint = this.allDestinations.filter(d => d.endpointName == 'Surefire3')[0];
          tmpIdentifier.destinationId = dest.destinationEndpointId;
          tmpIdentifier.destinationName = dest.endpointName;
        }
      }
    }

    if (!continueTesting) {
      this.toastService.toastCreate('Creating \'' + tmpIdentifier.destinationName + '\' identifier. Check form values are valid and click Create.', 'Info');
      this.selectedIdentifier = tmpIdentifier;
      this.inAddMode = true;
      this.showAddUpdateForm = true;
    }

    if (continueTesting) {
      this.toastService.toastCreate('Checked CSDataSwap, LGDataSwap and Surefire3 - identifiers for valid elements already created.', 'Info');
      this.generatedNewIdentifier = false;
      this.runningGeneratingIdentifiers = false;
    }
  }

  getDestinations(): void {
    this.typeService.getAllDestinationEndpoints()
    .pipe(takeUntil(this.componentDestroyed$))
    .subscribe(r => {
      this.allDestinations = r;
    },
    (_error) => {
      this.toastService.toastCreate('Error getting destinations: ' + _error.substr(0, 112), 'Warning');
    });
  }
}
